import Big from "big.js";
import { Text } from "components/commons";
import styles from "./detailed-transaction-history-list.module.scss";
import { dateTimeFormat, formatAmount, formatVolume } from "utils";

import { prettifyProductType, prettifyKeyPaymentMethod } from "utils/pretty.utils";
import DetailedTransactionHistoryRefNumbers from "./detailed-transaction-history-ref-numbers";

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const sortByItemId = (list) => [...(list ?? [])].sort((a, b) => a?.itemId - b?.itemId);

const grossAmountOf = (item) =>
  Number(
    Big(toNumber(item?.quantity))
      .times(toNumber(item?.originalPricePerItem))
      .round(2)
  );

const discountAmountOf = (item) => {
  if (item?.originalTotalAmount != null && item?.netTotalAmount != null) {
    return Number(
      Big(toNumber(item.originalTotalAmount)).minus(toNumber(item.netTotalAmount)).round(2)
    );
  }
  return Math.abs(toNumber(item?.totalDiscount));
};

const amountDueOf = (item) =>
  Number(Big(grossAmountOf(item)).minus(discountAmountOf(item)).round(2));

export const mapDataToList = ({ transactions, station }) => {
  const { items, processedAt, refName, receiptNumber, paymentMethods, shift } = transactions;

  const stationCode = station?.stationCode;
  const sortedItems = sortByItemId(items);
  const sortedPaymentMethods = sortByItemId(paymentMethods);

  const mapItems = (render) =>
    sortedItems.map((item, index) => (
      <Text key={`item-${index}`} className={styles.td}>
        {render(item)}
      </Text>
    ));

  return {
    stationCode: <Text className={styles.td}>{stationCode ?? ""}</Text>,
    shiftDateAndTime: (
      <Text className={styles.td}>
        {dateTimeFormat(shift?.startShift)} - {dateTimeFormat(shift?.closedAt)}
      </Text>
    ),
    salesInvoiceNumber: <Text className={styles.td}>{receiptNumber ?? ""}</Text>,
    transactionDateAndTime: <Text className={styles.td}>{dateTimeFormat(processedAt) ?? ""}</Text>,
    paymentMethod: (
      <>
        {sortedPaymentMethods.map((item, index) => (
          <Text key={`payment-method-${index}`} className={styles.td}>
            {prettifyKeyPaymentMethod(item?.method)}
          </Text>
        ))}
      </>
    ),
    paymentReferenceNumberAndName: (
      <DetailedTransactionHistoryRefNumbers
        paymentMethods={sortedPaymentMethods}
        refName={refName}
      />
    ),
    productType: <>{mapItems((item) => prettifyProductType(item?.type))}</>,
    productName: <>{mapItems((item) => item?.name)}</>,
    productDetails: (
      <>
        {mapItems((item) =>
          /^\d+$/.test(item?.variant) ? `Pump ${item?.variant}` : item?.variant
        )}
      </>
    ),
    quantity: (
      <>
        {mapItems((item) =>
          item?.type === "fuel" ? formatVolume(item?.quantity) : item?.quantity
        )}
      </>
    ),
    totalizerReading: <>{mapItems((item) => item?.lastTotalInventory ?? "--")}</>,
    pricePerItem: <>{mapItems((item) => formatAmount(item?.originalPricePerItem))}</>,
    totalGrossAmount: <>{mapItems((item) => formatAmount(grossAmountOf(item)))}</>,
    totalDiscount: <>{mapItems((item) => formatAmount(discountAmountOf(item)))}</>,
    totalAmountDue: <>{mapItems((item) => formatAmount(amountDueOf(item)))}</>,
  };
};

export const mapFilterToRequest = (filterState) => {
  const { statuses, ...fs } = filterState;

  return {
    ...fs,
    statuses: statuses.length === 0 ? [] : statuses.join(","),
  };
};
